import {
  forwardRef,
  Key,
  useCallback,
  useEffect,
  useImperativeHandle,
  useState,
} from "react";
import { FormControlChildProps } from "../form-control";
import AsyncSelect from "react-select/async";
import { OptionsOrGroups, SingleValue } from "react-select";
import { LabelValue } from "@app/types";
import { debounce } from "lodash";

export type PromiseOptionsCallback = (
  options: OptionsOrGroups<LabelValue, any>
) => void;

interface CustomAsyncSelectProps extends FormControlChildProps {
  placeholder?: string;

  className?: string;

  value?: LabelValue;

  defaultValue?: LabelValue;

  defaultStatus?: undefined | "valid" | "inValid" | "warn";

  errorClassName?: string;

  promiseOptions: (
    inputValue: string,
    callback: PromiseOptionsCallback
  ) => Promise<OptionsOrGroups<LabelValue, any>> | void;

  isClearable?: boolean;

  onChange?: (newValue: SingleValue<LabelValue>) => void;

  styles?: {
    stylesControl?: Record<string, string | number>;

    styleOption?: Record<string, string | number>;

    styleSingle?: Record<string, string | number>;

    dropdownIndicatorStyles?: Record<string, string | number>;
  };

  _key?: Key;
}

export type CustomAsyncSelectRef = {
  resetValue: () => void;
};

const CustomAsyncSelect = forwardRef<
  CustomAsyncSelectRef,
  CustomAsyncSelectProps
>(
  (
    {
      id,
      formik,
      placeholder,
      value,
      defaultValue = undefined,
      name,
      status,
      defaultStatus = undefined,
      errorClassName = "",
      className = "",
      promiseOptions,
      styles = {
        stylesControl: {},
        styleOption: {},
        styleSingle: {},
        dropdownIndicatorStyles: {},
      },
      isClearable = false,
      onChange,
      _key,
    },
    ref
  ) => {
    const [txValue, setTxValue] = useState<LabelValue | null | undefined>(
      value || defaultValue
    );
    const [_status, changeStatus] = useState(status || defaultStatus);

    const handleChange = useCallback(
      (newValue: SingleValue<LabelValue>) => {
        setTxValue(newValue);

        onChange && onChange(newValue);

        if (formik) {
          formik.setFieldValue(name!, newValue);
        }

        setTimeout(() => {
          if (formik) {
            formik.setFieldTouched(name!, true);
          }
        });
      },
      [formik, name]
    );

    useImperativeHandle(ref, () => ({
      resetValue: () => {
        setTxValue(null);
      },
    }));

    useEffect(() => {
      setTxValue(value || null);
    }, [value]);

    useEffect(() => {
      changeStatus(status || defaultStatus);
    }, [defaultStatus, status]);

    const isError = _status === "inValid" || _status === "warn";

    const _loadOptions = debounce(promiseOptions, 300);

    return (
      <div>
        <div className={`w-full ${className}`}>
          <AsyncSelect
            isClearable={isClearable}
            id={id}
            placeholder={placeholder}
            cacheOptions
            defaultOptions
            value={txValue}
            onChange={handleChange}
            loadOptions={_loadOptions}
            key={_key}
            styles={{
              control: (baseStyles) => ({
                ...baseStyles,
                cursor: "pointer",
                ...styles.stylesControl,
                borderColor: isError ? "#fc4040" : "",
              }),
              singleValue: (baseStyles) => ({
                ...baseStyles,
                ...styles.styleSingle,
              }),
              option: (baseStyles) => ({
                ...baseStyles,
                cursor: "pointer",
                ...styles.styleOption,
              }),
              dropdownIndicator: (baseStyles) => ({
                ...baseStyles,
                ...styles.dropdownIndicatorStyles,
              }),
            }}
          />
        </div>
        {isError && (
          <div className={`flex error-color text-xs ${errorClassName}`}>
            {formik &&
              formik.getFieldMeta(name!).error &&
              formik.getFieldMeta(name!).error}
          </div>
        )}
      </div>
    );
  }
);

CustomAsyncSelect.displayName = "CustomAsyncSelect";

export default CustomAsyncSelect;
